/**
 * Clerk's components restyled onto the application's own tokens.
 *
 * Clerk keeps the parts that are risky to reimplement — bot protection, verification codes,
 * new-device challenges — so its components are kept and restyled rather than replaced by a
 * hand-built form against the headless hooks.
 *
 * Colours are given as `var(--color-…)` on the element rules rather than through Clerk's
 * `variables`, because Clerk derives shades from `variables` numerically and cannot do that
 * with a custom property. Styling the elements instead means the light and dark themes swap
 * with the rest of the interface, from one set of definitions.
 */

import type { Appearance } from '@clerk/types';

/** Shared shape for a text input, matching `TextField` in the app's own controls. */
const INPUT = {
  backgroundColor: 'var(--color-surface)',
  borderColor: 'var(--color-line-strong)',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderRadius: 'var(--radius-control)',
  color: 'var(--color-ink)',
  fontSize: 'var(--text-base)',
  minHeight: '2.75rem',
  boxShadow: 'none',
  '&:focus-within': {
    borderColor: 'var(--color-brand)',
    boxShadow: '0 0 0 2px color-mix(in srgb, var(--color-brand) 35%, transparent)',
  },
} as const;

/** Shared shape for the primary action, matching `Button` with the primary tone. */
const PRIMARY_BUTTON = {
  backgroundColor: 'var(--color-brand)',
  color: 'var(--color-on-brand)',
  fontSize: 'var(--text-base)',
  fontWeight: '600',
  textTransform: 'none',
  minHeight: '2.75rem',
  borderRadius: 'var(--radius-control)',
  boxShadow: 'none',
  '&:hover': { backgroundColor: 'var(--color-brand-ink)' },
  '&:focus': { boxShadow: '0 0 0 2px var(--color-brand)' },
  '&:active': { backgroundColor: 'var(--color-brand-ink)' },
} as const;

/** Shared shape for secondary and social buttons. */
const SECONDARY_BUTTON = {
  backgroundColor: 'transparent',
  borderColor: 'var(--color-line-strong)',
  color: 'var(--color-ink)',
  fontSize: 'var(--text-base)',
  fontWeight: '600',
  textTransform: 'none',
  minHeight: '2.75rem',
  borderRadius: 'var(--radius-control)',
  boxShadow: 'none',
  '&:hover': { backgroundColor: 'var(--color-sunken)' },
} as const;

/**
 * The appearance passed to `ClerkProvider`.
 *
 * The card is stripped of its own frame because the page already provides one; what remains is
 * the form itself, sitting directly on the app's surface.
 */
export const CLERK_APPEARANCE: Appearance = {
  layout: {
    socialButtonsPlacement: 'bottom',
    showOptionalFields: false,
    animations: false,
  },
  variables: {
    fontFamily: 'var(--font-sans)',
    fontSize: '1rem',
    borderRadius: '0.625rem',
  },
  elements: {
    rootBox: { width: '100%' },
    cardBox: { width: '100%', boxShadow: 'none', border: 'none' },
    card: {
      width: '100%',
      backgroundColor: 'transparent',
      boxShadow: 'none',
      border: 'none',
      padding: '0',
      gap: '1.25rem',
    },

    // The page heading already names the task, so Clerk's own title is redundant.
    header: { display: 'none' },

    formFieldLabel: {
      fontSize: '0.875rem',
      fontWeight: '600',
      color: 'var(--color-ink-2)',
    },
    formFieldInput: INPUT,
    otpCodeFieldInput: {
      ...INPUT,
      width: '2.75rem',
      fontSize: 'var(--text-title)',
      textAlign: 'center',
    },
    formFieldAction: { color: 'var(--color-brand-text)', fontSize: 'var(--text-tiny)' },
    formFieldHintText: { color: 'var(--color-ink-3)', fontSize: 'var(--text-tiny)' },
    formFieldErrorText: { color: 'var(--color-missing)', fontSize: 'var(--text-tiny)' },
    formFieldSuccessText: { color: 'var(--color-attending)', fontSize: 'var(--text-tiny)' },
    formFieldWarningText: { color: 'var(--color-accent-ink)', fontSize: 'var(--text-tiny)' },

    formButtonPrimary: PRIMARY_BUTTON,
    formButtonReset: SECONDARY_BUTTON,
    socialButtonsBlockButton: SECONDARY_BUTTON,
    socialButtonsIconButton: SECONDARY_BUTTON,
    alternativeMethodsBlockButton: SECONDARY_BUTTON,
    backLink: { color: 'var(--color-brand-text)' },

    dividerLine: { backgroundColor: 'var(--color-line)' },
    dividerText: { color: 'var(--color-ink-3)', fontSize: 'var(--text-tiny)' },

    identityPreview: {
      backgroundColor: 'var(--color-sunken)',
      borderColor: 'var(--color-line)',
      borderRadius: 'var(--radius-control)',
    },
    identityPreviewText: { color: 'var(--color-ink)' },
    identityPreviewEditButton: { color: 'var(--color-brand-text)' },

    footer: {
      background: 'none',
      backgroundColor: 'transparent',
      borderTop: '1px solid var(--color-line)',
      paddingTop: '1rem',
      marginTop: '0.5rem',
    },
    footerAction: { fontSize: 'var(--text-base)' },
    footerActionText: { color: 'var(--color-ink-2)', fontSize: 'var(--text-base)' },
    footerActionLink: {
      color: 'var(--color-brand-text)',
      fontWeight: '600',
      '&:hover': { color: 'var(--color-brand-ink)' },
    },

    formResendCodeLink: { color: 'var(--color-brand-text)' },
    alertText: { color: 'var(--color-ink)', fontSize: 'var(--text-tiny)' },
    formHeaderTitle: { color: 'var(--color-ink)', fontSize: 'var(--text-title)' },
    formHeaderSubtitle: { color: 'var(--color-ink-2)', fontSize: 'var(--text-base)' },
    main: { gap: '1rem' },
  },
};
